import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Skeleton } from '@/shared/ui/Skeleton'

import { ReviewMediaItem } from '../../model/types'
import { ReviewMediaPreview } from '../media/ReviewMediaPreview'

import styles from './ReviewsMediaSlider.module.scss'

type ReviewsMediaSliderProps = {
	items: ReviewMediaItem[]
	total: number
	onOpen: (index: number) => void
	onOpenCatalogue: () => void
}

const MOBILE_VISIBLE_ITEMS_COUNT = 4
const DESKTOP_VISIBLE_ITEMS_COUNT = 10
const TABLET_PREVIEW_WIDTH = 80
const MEDIA_GAP = 5
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const useVisibleMediaItemsCount = (itemsLength = 0) => {
	const [visibleItemsCount, setVisibleItemsCount] = useState(4)
	const galleryRef = useRef<HTMLDivElement>(null)

	useIsomorphicLayoutEffect(() => {
		const gallery = galleryRef.current
		if (!gallery) return

		const updateVisibleItemsCount = () => {
			if (!window.matchMedia('(min-width: 481px)').matches) {
				setVisibleItemsCount(MOBILE_VISIBLE_ITEMS_COUNT)
				return
			}

			if (window.matchMedia('(min-width: 992px)').matches) {
				setVisibleItemsCount(DESKTOP_VISIBLE_ITEMS_COUNT)
				return
			}

			const availableWidth = gallery.clientWidth
			setVisibleItemsCount(Math.max(1, Math.floor((availableWidth + MEDIA_GAP) / (TABLET_PREVIEW_WIDTH + MEDIA_GAP))))
		}

		updateVisibleItemsCount()
		const resizeObserver = new ResizeObserver(updateVisibleItemsCount)
		resizeObserver.observe(gallery)
		return () => resizeObserver.disconnect()
	}, [itemsLength])

	return { galleryRef, visibleItemsCount }
}

export const ReviewsMediaSlider = ({ items, total, onOpen, onOpenCatalogue }: ReviewsMediaSliderProps) => {
	const { galleryRef, visibleItemsCount } = useVisibleMediaItemsCount(items.length)
	const displayedItems = items.slice(0, visibleItemsCount)
	const hiddenItemsCount = total > displayedItems.length ? total - displayedItems.length + 1 : 0

	return (
		<div ref={galleryRef} className={styles.gallery} aria-label="Фото и видео покупателей">
			<div className={styles.mediaList} style={{ '--review-media-columns': visibleItemsCount } as CSSProperties}>
				{displayedItems.map((item, index) => {
					const isMoreTile = hiddenItemsCount > 0 && index === displayedItems.length - 1
					return (
						<button
							type="button"
							className={styles.mediaOpenButton}
							key={item.id}
							onClick={() => {
								if (isMoreTile && window.matchMedia('(max-width: 991px)').matches) {
									onOpenCatalogue()
									return
								}

								onOpen(index)
							}}>
							<ReviewMediaPreview
								className={styles.mediaPreview}
								src={item.src}
								alt={`Материал покупателя ${index + 1}`}
							/>
							{isMoreTile && (
								<span className={styles.mediaMore} aria-label={`Еще ${hiddenItemsCount} материалов`}>
									+{hiddenItemsCount}
								</span>
							)}
						</button>
					)
				})}
			</div>
		</div>
	)
}

export const ReviewsMediaSliderSkeleton = () => {
	const { galleryRef, visibleItemsCount } = useVisibleMediaItemsCount()

	return (
		<div ref={galleryRef} className={styles.gallery} aria-hidden="true">
			<div
				className={styles.mediaSkeletonList}
				style={{ '--review-media-columns': visibleItemsCount } as CSSProperties}>
				{Array.from({ length: visibleItemsCount }).map((_, index) => (
					<Skeleton className={styles.mediaSkeletonItem} borderRadius={5} key={index} />
				))}
			</div>
		</div>
	)
}
